import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";

import DebugPanel from "./DebugPanel";
import UploadArea from "./UploadArea";
import ErrorMessage from "./ErrorMessage";
import SuccessMessage from "./SuccessMessage";
import JobDescriptionForm from "./JobDescriptionForm";
// import AnalysisLoading from "./AnalysisLoading"; // Commented out - replaced with LoadingModal
import AnalysisResults from "./AnalysisResults";
import QuotaIndicator from "./QuotaIndicator";
// import SubscriptionModal from "./SubscriptionModal";
import PackagesModal from "./PackagesModal";
import DuplicateJobWarning from "./DuplicateJobWarning";
import ApplicationJournal from "./ApplicationJournal";
import LoadingModal from "./LoadingModal";
import {
  extractPersonalInfoFromCV,
  saveExtractedCVInfo,
} from "../utils/cvDataExtractor";
import {
  getStoredCV,
  hasStoredCV,
  clearStoredCV,
} from "../utils/cvStorageUtils";
import {
  extractJobInfoFromDescription,
  checkDuplicateApplication,
  saveJobApplication,
  type JobInfo,
} from "../utils/jobExtractor";
import type {
  AnalysisOptions,
  FileInfo,
  FinalResults,
  ProgressStep, 
  UserSubscriptionDB,
} from "../types";
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import { supabase } from "../lib/supabase";
import { Subscription } from "../stores/subscription.store";
import { useProfile } from "../hooks/useProfile";

interface AnalyzerProps {
  subscription: Subscription | null;
}

const AnalyzerContainer: React.FC<AnalyzerProps> = ({ subscription }) => {
  const { t, i18n } = useTranslation();
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [jobDescription, setJobDescription] = useState<string>("");
  const [analysisInProgress, setAnalysisInProgress] = useState<boolean>(false);
  const [analysisResults, setAnalysisResults] = useState<FinalResults | null>(
    null
  );
  const [file, setFile] = useState<File | null>(null);
  const [cvFileUrl, setCvFileUrl] = useState<string | null>(null);
  const [analysisOptions, setAnalysisOptions] = useState<AnalysisOptions>({
    analyzeSkills: true,
    analyzeExperience: true,
    analyzeEducation: true,
    analyzeCertifications: true,
    analyzeLanguages: true,
    generateRecommendations: true,
  });


  // Progress steps used by real-time updates
  const [, setProgressSteps] = useState<ProgressStep[]>([
    { id: "step1", text: t("analyzer.cv_extraction"), status: "waiting" },
    { id: "step2", text: t("analyzer.job_parsing"), status: "waiting" },
    { id: "step3", text: t("analyzer.semantic_analysis"), status: "waiting" },
    { id: "step4", text: t("analyzer.score_calculation"), status: "waiting" },
  ]);
  const [currentStepMessage, setCurrentStepMessage] = useState<string>("");

  // Subscription state from DB
  const { refetch } = useSubscription();
  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);

  const { addLogsToProfile, removeLogs, profile, fetchProfile } = useProfile();

  // Check for payment success and auto-open subscription modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const paymentCancelled = urlParams.get('payment_cancelled');
    
    
    if (paymentSuccess === 'true') {
      setShowSubscriptionModal(true);
    } else if (paymentCancelled === 'true') {
      setShowSubscriptionModal(true);
    }
  }, []);

  // États pour la détection de poste
  const [currentJobInfo, setCurrentJobInfo] = useState<JobInfo | null>(null);
  const [showDuplicateWarning, setShowDuplicateWarning] =
    useState<boolean>(false);

  // État pour le journal des candidatures
  const [showApplicationJournal, setShowApplicationJournal] =
    useState<boolean>(false);

  const { user, session, loading: authLoading } = useAuth();
  // console.log("SESSION in  : ",session,user);

  const [analyzeButtonLocked, setAnalyzeButtonLocked] = useState(false);
  const [outputId, setOutputId] = useState<string>("");
  const [, setAbortController] = useState<AbortController | null>(null);
  const [realtimeChannel, setRealtimeChannel] = useState<any>(null);

  // Analysis history state
  const [analysisHistory, setAnalysisHistory] = useState<any[]>([]);
  const [cvScoreHistory, setCvScoreHistory] = useState<any>(null);
  const [analysisHistoryLoading, setAnalysisHistoryLoading] = useState(false);

  // Vérifier si un CV est déjà stocké au chargement
  useEffect(() => {
    if (hasStoredCV()) {
      const storedCV = getStoredCV();
      if (storedCV) {
        setFileContent(storedCV.content);
        setFileInfo({
          name: storedCV.fileName,
          size: storedCV.fileSize,
          type: getFileTypeFromName(storedCV.fileName),
          lastModified: new Date(storedCV.uploadDate).getTime(),
        });
        showMessage(
          t("analyzer.cv_loaded_from_profile", { fileName: storedCV.fileName }),
          "success"
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, []);

  // Fetch analysis history when user is authenticated
  useEffect(() => {
    if (session?.access_token && user?.id) {
      fetchAnalysisHistory();
    }
  }, [session?.access_token, user?.id]);

  // Cleanup realtime channel on unmount
  useEffect(() => {
    return () => {
      if (realtimeChannel) {
        supabase.removeChannel(realtimeChannel);
      }
    };
  }, [realtimeChannel]);

  // IMPORTANT: Do NOT call refetch in a useEffect with [refetch] or [subscription] dependencies.
  // Only call refetch() in response to user actions (e.g., after analysis, after upgrade, after upload).
  // This prevents repeated or infinite API calls.

  // Use only DB subscription object for all plan logic and display
  const currentPlan = subscription;
  const quota = {
    current: subscription?.analyses_used || 0,
    limit:
      (subscription?.analyses_remaining || 0) +
      (subscription?.analyses_used || 0), // Ensure limit is consistent
    percentage: Math.round(
      ((subscription?.analyses_used || 0) /
        Math.max(
          (subscription?.analyses_remaining || 0) +
            (subscription?.analyses_used || 0),
          subscription?.analyses_used || 0
        )) *
        100
    ),
    canAnalyze:
      (subscription?.analyses_remaining || 0) > 0 && subscription?.is_active,
  };

  const formatCurrentDateTime = (): string => {
    const now = new Date();
    const time = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${time}`;
  };

  const showMessage = (message: string, type: "error" | "success") => {
    if (type === "error") {
      setErrorMessage(message);
      setTimeout(() => setErrorMessage(null), 5000);
    } else {
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const clearLogs = async () => {
    const confirmed = window.confirm(
      `${t("debug_panel.deleteLogs")}\n${t("debug_panel.cantBeRecovered")}`
    );
    if (confirmed) {
      await removeLogs();
    }
  };

  // Fetch analysis history from API
  const fetchAnalysisHistory = async () => {
    if (!session?.access_token) {
      console.warn('No access token available for fetching analysis history');
      return;
    }

    try {
      setAnalysisHistoryLoading(true);
      
      const response = await axios.get(
        API_ENDPOINTS.GET_CV_ANALYSIS_HISTORY,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.code === 200 && response.data.status === 'success') {
        setAnalysisHistory(response.data.data.analysisHistory);
        setCvScoreHistory(response.data.data.cvScoreHistory[0] || null);
      }
    } catch (error: any) {
      console.error('Error fetching analysis history:', error);
      // Don't show error to user as this is background loading
    } finally {
      setAnalysisHistoryLoading(false);
    }
  };

  const resetUpload = () => {
    setFileInfo(null);
    setFileContent(null);
    setAnalysisResults(null);
    setFile(null);
    setCvFileUrl(null);
  };

  // NOUVELLE FONCTION: Gérer le changement de CV
  const handleChangeCV = () => {
    // Supprimer le CV stocké
    clearStoredCV();

    // Réinitialiser tous les états liés au CV
    setFileInfo(null);
    setFileContent(null);
    setAnalysisResults(null);
    setFile(null);
    setCvFileUrl(null);

    // Réinitialiser les autres états si nécessaire
    setJobDescription("");
    setCurrentJobInfo(null);
    setShowDuplicateWarning(false);

    showMessage(t("analyzer.cv_deleted"), "success");
  };

  const resetToInitialState = async () => {
    // Clean up realtime channel
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      setRealtimeChannel(null);
    }
    
    setFileInfo(null);
    setFileContent(null);
    setFile(null);
    setCvFileUrl(null);
    setAnalysisResults(null);
    setJobDescription("");
    setCurrentJobInfo(null);
    setShowDuplicateWarning(false);
    setAnalyzeButtonLocked(false);
    setAnalysisInProgress(false);
    setOutputId("");
    setAbortController(null);
    setAnalysisOptions({
      analyzeSkills: true,
      analyzeExperience: true,
      analyzeEducation: true,
      analyzeCertifications: true,
      analyzeLanguages: true,
      generateRecommendations: true,
    });
    // Commented out - no longer using progress steps
    setProgressSteps([
      { id: "step1", text: t("analyzer.cv_extraction"), status: "waiting" },
      { id: "step2", text: t("analyzer.job_parsing"), status: "waiting" },
      { id: "step3", text: t("analyzer.semantic_analysis"), status: "waiting" },
      { id: "step4", text: t("analyzer.score_calculation"), status: "waiting" },
    ]);
    setCurrentStepMessage("");
    // Removed step message handling since progress steps are disabled

    // Wait for state updates to complete
    await new Promise(resolve => setTimeout(resolve, 100));

    // Recharger le CV stocké s'il existe
    if (hasStoredCV()) {
      const storedCV = getStoredCV();
      if (storedCV) {
        setFileContent(storedCV.content);
        setFileInfo({
          name: storedCV.fileName,
          size: storedCV.fileSize,
          type: getFileTypeFromName(storedCV.fileName),
          lastModified: new Date(storedCV.uploadDate).getTime(),
        });
        showMessage(
          t("analyzer.cv_loaded_from_profile", { fileName: storedCV.fileName }),
          "success"
        );
      }
    }

    showMessage(t("analyzer.ready_for_new_analysis"), "success");
  };

  const handleJobDescriptionChange = (value: string) => {
    setJobDescription(value);

    // Extraire automatiquement les informations du poste
    if (value.trim().length > 50) {
      const jobInfo = extractJobInfoFromDescription(value, t);
      setCurrentJobInfo(jobInfo);

      // Vérifier si c'est un doublon
      if (jobInfo.id && checkDuplicateApplication(jobInfo.id)) {
        setShowDuplicateWarning(true);
      } else {
        setShowDuplicateWarning(false);
      }
    } else {
      setCurrentJobInfo(null);
      setShowDuplicateWarning(false);
    }
  };

  const isAnalysisReady = (): boolean => {
    const hasCV = !!(file || cvFileUrl || fileContent);
    const hasUser = user !== null && !authLoading;
    const hasJobDescription = jobDescription.trim() !== "";
    const hasOptions = Object.values(analysisOptions).some(value => value);
    const notInProgress = !analysisInProgress;
    const hasQuota = !!quota.canAnalyze;

    return (
      hasCV &&
      hasUser &&
      hasJobDescription &&
      hasOptions &&
      notInProgress &&
      hasQuota
    );
  };

  // Progress steps are now updated via real-time Supabase updates

  const startRealtimeUpdates = (outputId: string) => {
    // Clean up any existing channel
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
    }

    const channel = supabase
      .channel(`cv_analysis_${outputId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "cv_output",
          filter: `id=eq.${outputId}`,
        },
        async (payload) => {
          const newData = payload.new;
          
          // Update progress steps based on step_id
          if (newData.step_id) {
            const stepMessages = {
              step1: t("analyzer.cv_extraction") + "...",
              step2: t("analyzer.job_parsing") + "...",
              step3: t("analyzer.semantic_analysis") + "...",
              step4: t("analyzer.score_calculation") + "...",
            };
            
            setProgressSteps(prev =>
              prev.map(step => ({
                ...step,
                status:
                  step.id === newData.step_id
                    ? "active"
                    : prev.findIndex(s => s.id === step.id) <
                      prev.findIndex(s => s.id === newData.step_id)
                    ? "completed"
                    : "waiting",
              }))
            );
            
            setCurrentStepMessage(
              stepMessages[newData.step_id as keyof typeof stepMessages] || ""
            );
          }

          // Check if analysis is completed (responseData is available)
          if (newData.responseData && typeof newData.responseData === 'object' && !newData.responseData.error) {
            try {
              const results = newData.responseData;
              setAnalysisResults(results);
              showMessage(t("analyzer.analysis_complete"), "success");

              // Handle user info logging if present
              if (results?.userInfo) {
                const userInfo = results.userInfo;
                const positionTitle = userInfo?.positionTitle;
                const city = userInfo?.city;
                const currentDate = formatCurrentDateTime();

                if (
                  !profile ||
                  (Array.isArray(profile.logs) && profile.logs.length < 50)
                ) {
                  const logMessage = positionTitle
                    ? `${currentDate} [${t("debug_panel.info")}] ${positionTitle} ${
                        city ? `${t("debug_panel.in")} ${city}` : ""
                      }`
                    : `${currentDate} [${t("debug_panel.info")}] ${t(
                        "debug_panel.analysisDone"
                      )}`;

                  await addLogsToProfile(logMessage);
                }
              }

              if (currentJobInfo) {
                saveJobApplication(currentJobInfo);
              }

              // Refetch subscription data to update analyses_remaining across the app
              await refetch();
              
              // Fetch analysis history after subscription is updated
              fetchAnalysisHistory();
              
              // Stop analysis state
              setAnalysisInProgress(false);
              setAnalyzeButtonLocked(false);
              setAbortController(null);
              
              // Clean up channel
              supabase.removeChannel(channel);
              setRealtimeChannel(null);
              
            } catch (error) {
              console.error('Error processing completed analysis:', error);
              showMessage(t("analyzer.analysis_failed") + ": " + error, "error");
              setAnalysisInProgress(false);
              setAnalyzeButtonLocked(false);
              setAbortController(null);
              
              // Clean up channel
              supabase.removeChannel(channel);
              setRealtimeChannel(null);
            }
          }

          // Check if analysis failed
          if (newData.responseData) {
            let responseData = newData.responseData;
            
            // Handle case where responseData might be a string
            if (typeof responseData === 'string') {
              try {
                responseData = JSON.parse(responseData);
              } catch (e) {
                console.log('Failed to parse responseData as JSON:', responseData);
              }
            }
            
            if (responseData && responseData.error) {
              console.log('Error detected in responseData:', responseData);
              const errorMsg = responseData.error || "Unknown error";
              console.log('Showing error message:', errorMsg);
              showMessage(`${t("analyzer.analysis_failed")}: ${errorMsg}`, "error");
              setAnalysisInProgress(false);
              setAnalyzeButtonLocked(false);
              setAbortController(null);
              
              // Clean up channel
              supabase.removeChannel(channel);
              setRealtimeChannel(null);
            }
          }
        }
      )
      .subscribe();

    setRealtimeChannel(channel);
  };

  const performAnalysis = async () => {
    if (
      analysisInProgress ||
      analyzeButtonLocked ||
      (!file && !cvFileUrl && !fileContent) ||
      !jobDescription.trim()
    ) {
      return;
    }
    setAnalyzeButtonLocked(true);
    if (!quota.canAnalyze) {
      showMessage(t("analyzer.insufficient_quota"), "error");
      setShowSubscriptionModal(true);
      setAnalyzeButtonLocked(false);
      return;
    }
    // REMOVE: const success = consumeAnalysis();
    // REMOVE: if (!success) { ... }
    // The backend will decrement analyses; rely on real-time update
    try {
      setAnalysisInProgress(true);
      // Reset step message to show default loading message first
      setCurrentStepMessage("");
      // Prepare abort controller for this request
      const controller = new AbortController();
      setAbortController(controller);

      const formData = new FormData();
      if (cvFileUrl) {
        formData.append("cvFileUrl", cvFileUrl);
      } else {
        formData.append("file", file as File);
      }
      formData.append("jobDescription", jobDescription);
      formData.append("analysisOptions", JSON.stringify(analysisOptions));
      formData.append("userId", user?.id || "");
      formData.append(
        "language",
        i18n.language === "fr" ? "french" : "english"
      );

      const response = await axios.post(API_ENDPOINTS.UPLOAD_CV, formData, { 
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        }
      });
      
      // Check if we got the immediate response with outputId
      if (response?.data?.status === "success" && response?.data?.outputId) {
        const outputId = response.data.outputId;
        setOutputId(outputId);
        
        // Start listening for real-time updates
        startRealtimeUpdates(outputId);
        // Don't set analysisInProgress to false here - let real-time updates handle it
        return; // Exit here - results will come via real-time updates
      }

      // If we didn't get the expected immediate response, handle as error
      setErrorMessage(response?.data?.error || "Unexpected response format")
      throw new Error(
        `Server error: ${response?.data?.message || "Unexpected response format"}`
      );
    } catch (error: any) {
      // Do not show error if the request was intentionally aborted
      const aborted = (typeof (axios as any).isCancel === "function" && (axios as any).isCancel(error))
        || error?.code === "ERR_CANCELED"
        || error?.name === "CanceledError"
        || error?.name === "AbortError";

      if (!aborted) {
        const apiError = error?.response?.data?.error;
        const message = typeof apiError === "string" && apiError.trim()
          ? apiError
          : (error?.message || "Unknown error");
        showMessage(`${t("analyzer.analysis_failed")}: ${message}`, "error");
      }
      
      // Only reset states if there was an actual error (not successful immediate response)
      setAnalysisInProgress(false);
      setAbortController(null);
      setAnalyzeButtonLocked(false);
    }
  };

  const handleUpgrade = () => {
    refetch();
    showMessage(t("analyzer.upgrade_required"), "success");
  };

  const getAnalyzeButtonText = (): string => {
    if (authLoading) {
      return "⏳ " + t("analyzer.loading");
    }

    if (analysisInProgress) {
      return "🔄 " + t("analyzer.analysis_in_progress");
    }

    if (!quota.canAnalyze) {
      return "⚠️ " + t("analyzer.insufficient_quota");
    }

    if (!(file || cvFileUrl)) {
      return hasStoredCV()
        ? "⚠️ " + t("job_description.required")
        : "⚠️ " + t("upload.title");
    }

    if (!jobDescription.trim()) {
      return "⚠️ " + t("job_description.required");
    }

    if (!Object.values(analysisOptions).some(value => value)) {
      return "⚠️ " + t("analyzer.info");
    }

    // If analysis has already been performed, change button text
    if (analysisResults) {
      return "🚀 " + t("analyzer.new_analysis");
    }

    return "🚀 " + t("analyzer.analyze");
  };

  const handleFileContentChange = (
    content: string | null,
    info: FileInfo | null,
    file: File | null
  ) => {
    if ((file || (content && typeof content === "string")) && info) {
      setFileContent(content || null);
      setFileInfo(info);
      setFile(file);
      // Detect if content is a URL (profile CV) or S3 key
      if (
        !file &&
        typeof content === "string" &&
        (/^https?:\/\//.test(content) || content.includes('/'))
      ) {
        setCvFileUrl(content);
      } else {
        setCvFileUrl(null);
      }
      showMessage(t("upload.upload_success"), "success");
      try {
        const personalInfo = extractPersonalInfoFromCV(content || "");
        if (personalInfo.name || personalInfo.email || personalInfo.phone) {
          saveExtractedCVInfo(personalInfo);
        }
      } catch (error) {}
    }
  };

  const handleNewAnalysisClick = async () => {
    if (analysisResults) {
      // If results exist, return to initial screen and wait for it to complete
      await resetToInitialState();
      // Additional wait to ensure all state updates are processed
      await new Promise(resolve => setTimeout(resolve, 300));
    } else {
      // Otherwise, launch normal analysis
      performAnalysis();
    }
  };

  const handleProceedWithDuplicate = () => {
    setShowDuplicateWarning(false);
    performAnalysis();
  };

  const getFileTypeFromName = (fileName: string): string => {
    const lowerFileName = fileName.toLowerCase();
    if (lowerFileName.endsWith(".pdf")) return "application/pdf";
    if (lowerFileName.endsWith(".doc")) return "application/msword";
    if (lowerFileName.endsWith(".docx"))
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (lowerFileName.endsWith(".txt")) return "text/plain";
    return "application/octet-stream";
  };

  // Determine if we should show upload area or stored CV indicator
  const shouldShowUploadArea =
    !hasStoredCV() || (!file && !cvFileUrl && !fileContent && !fileInfo);

  // Real-time updates are now handled by the startRealtimeUpdates function

  return (
    <div className="px-2 py-4 sm:px-4 md:px-8 md:py-8  mx-auto w-full">
      {/* <DebugPanel logs={profile?.logs || []} onClear={clearLogs} /> */}

      {currentPlan && (
        <QuotaIndicator
          quota={quota}
          currentPlan={subscription as any as UserSubscriptionDB}
          onUpgradeClick={() => setShowSubscriptionModal(true)}
        />
      )}

      <ApplicationJournal
        isOpen={showApplicationJournal}
        onToggle={() => setShowApplicationJournal(!showApplicationJournal)}
        analysisHistory={analysisHistory}
        cvScoreHistory={cvScoreHistory}
        loading={analysisHistoryLoading}
        session={session}
        onRefreshHistory={fetchAnalysisHistory}
      />

      {/* Show upload area if no stored CV OR if CV was deleted */}
      {shouldShowUploadArea && (
        <div className="w-full mx-auto">
          <UploadArea
            onFileContentChange={handleFileContentChange}
            fileInfo={fileInfo}
            onReset={resetUpload}
            showMessage={showMessage}
          />
        </div>
      )}

      {/* Show stored CV indicator if CV exists */}
      {!shouldShowUploadArea && fileInfo && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center  ">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-green-600 text-lg">📄</span>
              </div>
              <div>
                <h3 className="font-medium text-green-900 break-all text-base sm:text-lg">
                  {fileInfo.name}
                </h3>
                <p className="text-xs sm:text-sm text-green-700">
                  {(fileInfo.size / 1024).toFixed(1)} KB •{" "}
                  {fileInfo.lastModified
                    ? new Date(fileInfo.lastModified).toLocaleDateString()
                    : ""}
                </p>
              </div>
            </div>
            <div className="flex space-x-2 w-full sm:w-auto justify-end">
              <button
                onClick={handleChangeCV}
                className="w-full sm:w-auto px-3 py-2 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                {t("analyzer.change_cv")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Description Form */}
      {/* {(file || cvFileUrl) && ( */}
        <div className="w-full">
          <JobDescriptionForm
            jobDescription={jobDescription}
            onJobDescriptionChange={handleJobDescriptionChange}
            onAnalyze={handleNewAnalysisClick}
            analysisOptions={analysisOptions}
            isAnalysisReady={!!isAnalysisReady()}
            isAnalysisInProgress={analysisInProgress}
            analyzeButtonText={getAnalyzeButtonText()}
            onUpgradeClick={() => setShowSubscriptionModal(true)}
            showUpgradePrompt={!quota.canAnalyze}
          />
        </div>
      {/* )} */}

      {analysisInProgress && <LoadingModal
        currentStepMessage={currentStepMessage}
      />}
      {analysisResults && !analysisInProgress && (
        <div className="w-full">
          <AnalysisResults results={analysisResults} outputId={outputId} />
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="w-full">
          <ErrorMessage message={errorMessage} />
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="w-full">
          <SuccessMessage message={successMessage} />
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {showDuplicateWarning && currentJobInfo && (
        <DuplicateJobWarning
          jobInfo={currentJobInfo}
          onProceed={handleProceedWithDuplicate}
          onCancel={() => setShowDuplicateWarning(false)}
        />
      )}

      {/* Subscription Modal */}
      <PackagesModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onUpgrade={handleUpgrade}
        currentPlan={currentPlan?.plan as string}
        remainingAnalyses={subscription?.analyses_remaining ?? 0}
      />
    </div>
  );
};

export default AnalyzerContainer;
